/**
 * Phone Number Normalization Utility
 * 
 * Normalizes phone numbers to E.164 format for Supabase Auth phone OTP
 * 
 * Requirements:
 * - Phone numbers must be in E.164 format (+12345678901) for Supabase Auth
 * - This utility handles US/Canada numbers and validates format
 * 
 * Created: 2026-01-19
 * Purpose: Fix phone OTP authentication by ensuring phone numbers are stored in Supabase auth.users table
 */

import { parsePhoneNumberFromString } from 'libphonenumber-js';

export interface PhoneNormalizationResult {
  valid: boolean;
  normalized?: string;
  error?: string;
}

/**
 * Normalize phone number to E.164 format for Supabase Auth
 * 
 * @param phone - Phone number string (can be in various formats)
 * @returns Normalization result with E.164 formatted phone or error
 * 
 * @example
 * normalizePhoneForAuth('1234567890') // Returns: { valid: true, normalized: '+11234567890' }
 * normalizePhoneForAuth('+11234567890') // Returns: { valid: true, normalized: '+11234567890' }
 * normalizePhoneForAuth('invalid') // Returns: { valid: false, error: '...' }
 */
export function normalizePhoneForAuth(phone: string | null | undefined): PhoneNormalizationResult {
  // Handle empty/null/undefined
  if (!phone || !phone.trim()) {
    return {
      valid: false,
      error: 'Phone number is required'
    };
  }

  try {
    // Remove all non-numeric characters except +
    let cleanPhone = phone.replace(/[^\d+]/g, '');
    
    // If it doesn't start with +, handle US/Canada country code
    if (!cleanPhone.startsWith('+')) {
      // If they provided 11 digits starting with 1, just add the +
      if (cleanPhone.length === 11 && cleanPhone.startsWith('1')) {
        cleanPhone = '+' + cleanPhone;
      } else {
        // Otherwise assume it's a 10-digit US number and prepend +1
        cleanPhone = '+1' + cleanPhone;
      }
    }
    
    const phoneNumber = parsePhoneNumberFromString(cleanPhone);
    if (!phoneNumber || !phoneNumber.isValid()) {
      return { 
        valid: false, 
        error: 'Invalid phone number. Please enter a 10-digit US phone number (e.g., 1234567890)' 
      };
    }
    
    return { 
      valid: true, 
      normalized: phoneNumber.format('E.164') // Returns +12345678901
    };
  } catch (err) {
    return { 
      valid: false, 
      error: 'Invalid phone number format' 
    };
  }
}
